import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CloseOutlined } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadDoneOutlined from '@mui/icons-material/FileDownloadDoneOutlined';
import { Box } from '@mui/material';

import GroupedSelect from '@/components/GroupedSelect';
import { Option } from '@/components/GroupedSelect/types';
import Heading from '@/features/Dashboard/components/Heading';
import { Preview } from '@/features/Dashboard/components/Preview';
import { MAX_TILES_LIMIT } from '@/features/Dashboard/constants/const';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { useWidgetAnimation } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import { useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { DrawerBody } from '@/features/Dashboard/drawer/DrawerBody';
import { DrawerButton } from '@/features/Dashboard/drawer/DrawerButton';
import { DrawerHeader } from '@/features/Dashboard/drawer/DrawerHeader';
import { useWidgetOptions } from '@/features/Dashboard/hooks/useWidgetOptions';
import { MarketApps } from '@/features/Dashboard/types';
import getWidgetType from '@/features/Dashboard/utils/getWidgetType';
import { useDashboard, useUpdateDashboard } from '@/hooks/api/dashboard';
import { useMarketWidgets } from '@/hooks/api/market';
import { useAppSelector } from '@/hooks/redux-hooks';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';

// Helpers moved to separate util for simpler testing
import { buildImportedAppFromWidget, computeNextApps } from './utils';

const groupedSelectSx = {
  justifyContent: 'space-between',
  '& > .label__container': {
    width: 'auto'
  }
} as const;

export const ImportMarketWidget = () => {
  const [select, setSelect] = useState<{
    market: string | null;
    tab: string | null;
    widget: string | null;
  }>({
    market: null,
    tab: null,
    widget: null
  });

  const [mutationResult, setMutationResult] = useState<{
    duplicatesCount: number;
    addedCodes: string[];
  } | null>(null);

  const groupedMarkets = useAppSelector(state => state.marketsList.groupedMarkets);

  const marketsOptions = useMemo(
    () =>
      groupedMarkets
        .map(group => {
          return {
            label: group.name,
            options: group.markets
              .filter(m => !m.comingSoon)
              .map(market => ({
                label: market.name,
                value: market.marketId as string
              }))
          };
        })
        .filter(group => group.options.length > 0),
    [groupedMarkets]
  );

  const { data, isLoading } = useMarketWidgets(select.market);

  const tabOptions = useMemo(() => {
    if (!data) return [];
    return data.map(tab => ({
      label: tab.name,
      value: tab.name
    }));
  }, [data]);

  const selectedTab = useMemo(() => {
    if (!data) return;
    return data.find(tab => tab.name === select.tab);
  }, [data, select.tab]);

  const widgetOptions = useWidgetOptions(selectedTab);

  const handleMarketChange = ({ value }: Option) => {
    if (isPending) return;
    setSelect(() => ({ market: value, tab: null, widget: null }));
    setHasChanges(true);
  };

  const handleTabChange = ({ value }: Option) => {
    if (isPending) return;
    setSelect(prev => ({ ...prev, tab: value, widget: null }));
    setHasChanges(true);
  };

  const handleWidgetChange = ({ value }: Option) => {
    if (isPending) return;
    setSelect(prev => ({ ...prev, widget: value }));
    setHasChanges(true);
  };

  const selectedWidget = useMemo(() => {
    if (!selectedTab || !select.widget) return null;

    return (
      selectedTab.widgets.find(w => `${w.name}-{${getWidgetType(w)}}` === select.widget) || null
    );
  }, [selectedTab, select.widget]);

  const { data: allDashboards } = useDashboard();

  const dashboardName = Object.keys(allDashboards || {})[0];

  const { mutate, isSuccess, isPending } = useUpdateDashboard(
    dashboardName,
    '' // Success message handled manually in handleSave
  );

  const { closeDrawer, setHasChanges, setPreventClose, handleAttemptBack, isEditMode } =
    useDrawerSteps();
  const { addWidget } = useEditDashboardDraft();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();

  useEffect(() => {
    // Block drawer closing on outside click while update is pending
    setPreventClose(isPending);
  }, [isPending, setPreventClose]);

  useEffect(() => {
    if (isSuccess) {
      // Show success message with counts after mutation succeeds
      if (mutationResult) {
        if (mutationResult.duplicatesCount > 0 || mutationResult.addedCodes.length > 0) {
          const parts: string[] = [];
          if (mutationResult.duplicatesCount > 0) {
            parts.push(
              `${mutationResult.duplicatesCount} item${mutationResult.duplicatesCount > 1 ? 's' : ''} already in Dashboard`
            );
          }
          if (mutationResult.addedCodes.length > 0) {
            parts.push(
              `${mutationResult.addedCodes.length} item${mutationResult.addedCodes.length > 1 ? 's' : ''} added!`
            );
          }
          showSuccessSnackbar(parts.join(', '));
        } else {
          showSuccessSnackbar('Dashboard updated!');
        }
        // Clear the result after showing the notification
        setMutationResult(null);
      }
      closeDrawer();
    }
  }, [isSuccess, closeDrawer, mutationResult, showSuccessSnackbar]);

  const { setElementsToAnimateId } = useWidgetAnimation();

  const dashboardConfig = useMemo(() => {
    return allDashboards?.[dashboardName] || [];
  }, [allDashboards, dashboardName]);

  const templateWidget = useMemo(() => {
    if (!selectedWidget) return;

    return buildImportedAppFromWidget(selectedWidget as any, dashboardConfig);
  }, [dashboardConfig, selectedWidget]);

  const templateWidgetWithoutId = useMemo(() => {
    if (!templateWidget) return;
    if ('type' in templateWidget) {
      return { ...templateWidget, tiles: templateWidget.tiles.map(tile => ({ ...tile, id: '' })) };
    }
    return { ...templateWidget, id: '' };
  }, [templateWidget]);

  const handleSave = useCallback(() => {
    if (!templateWidget) return;

    if ('type' in templateWidget) {
      setElementsToAnimateId(templateWidget.tiles.map(tile => tile.id));
    } else {
      setElementsToAnimateId([templateWidget.id]);
    }
    // In edit mode, update the draft config instead of mutating via API
    if (isEditMode) {
      const result = addWidget(templateWidget);

      // Show messages about duplicates and added tiles
      if (result && 'exceeded' in result) {
        if (result.duplicatesCount > 0) {
          const duplicatesLabel = `Duplicate Highlighted price${result.duplicatesCount > 1 ? 's' : ''}`;
          showErrorSnackbar(
            `${duplicatesLabel} skipped: ${result.duplicateCodes.join(', ')}`,
            5000,
            'tiles-duplicates-error'
          );
        }

        if (result.addedTiles.length > 0) {
          const addedCodes = result.addedTiles.map(tile => tile.symbol.key.Code);
          const addedLabel = `Highlighted price${addedCodes.length > 1 ? 's' : ''}`;
          showSuccessSnackbar(`${addedLabel} added: ${addedCodes.join(', ')}`);
        }

        if (result.exceeded) {
          const tilesLabel = `tile${result.limitedCount === 1 ? '' : 's'}`;
          showErrorSnackbar(
            `Tile limit of ${MAX_TILES_LIMIT} exceeded. Only ${result.limitedCount} ${tilesLabel} added.`,
            5000,
            'tiles-limit-exceeded-error'
          );
        }
      }

      closeDrawer();
      return;
    }

    if (isPending) return;

    const result = computeNextApps(dashboardConfig as MarketApps | undefined, templateWidget);

    if (result.exceeded) {
      const tilesLabel = `tile${result.limitedCount === 1 ? '' : 's'}`;
      showErrorSnackbar(
        `Tile limit of ${MAX_TILES_LIMIT} exceeded. Only ${result.limitedCount} ${tilesLabel} added: ${result.addedCodes.join(', ')}`,
        5000,
        'tiles-limit-exceeded-error'
      );
    }

    // Store result for success notification
    setMutationResult({
      duplicatesCount: result.duplicatesCount,
      addedCodes: result.addedCodes
    });

    // Call mutate which will show the loading snackbar
    mutate(result.apps);
  }, [
    addWidget,
    closeDrawer,
    dashboardConfig,
    isEditMode,
    isPending,
    mutate,
    setElementsToAnimateId,
    showErrorSnackbar,
    showSuccessSnackbar,
    templateWidget
  ]);

  return (
    <>
      <DrawerHeader
        before={
          <DrawerButton
            disabled={isPending}
            icon={<ArrowBackIcon />}
            title='Go back'
            variant='secondary'
            onClick={handleAttemptBack}
          />
        }
        buttons={
          <>
            <DrawerButton
              disabled={!selectedWidget || isPending}
              icon={<FileDownloadDoneOutlined />}
              title='Save'
              onClick={handleSave}
            />
            <DrawerButton
              disabled={isPending}
              icon={<CloseOutlined />}
              title='Discard'
              variant='secondary'
              onClick={() => {
                if (!isPending) closeDrawer();
              }}
            />
          </>
        }
        title='Add new app'
      />
      <DrawerBody>
        <Heading
          subtitle='Select your market widget'
          title='Configure import market widget'
        />
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            '.label__container': {
              fontWeight: 700,
              fontSize: '14px'
            }
          }}
        >
          <GroupedSelect
            disabled={isPending}
            label='Select market'
            options={marketsOptions}
            placeholder='Select market'
            sx={groupedSelectSx}
            value={select.market}
            onChange={handleMarketChange}
          />
          <GroupedSelect
            disabled={isPending || !select.market || tabOptions.length === 0}
            isLoading={isLoading}
            label='Select page'
            options={tabOptions}
            placeholder='Select page'
            sx={groupedSelectSx}
            value={select.tab}
            onChange={handleTabChange}
          />
          <GroupedSelect
            disabled={isPending || !select.tab}
            isLoading={isLoading}
            label='Select widget'
            options={widgetOptions}
            placeholder='Select widget'
            sx={groupedSelectSx}
            value={select.widget}
            onChange={handleWidgetChange}
          />
          <Preview
            enabled={!!templateWidgetWithoutId}
            template={templateWidgetWithoutId}
          />
        </Box>
      </DrawerBody>
    </>
  );
};
